<script lang="ts" setup>
import { ref, computed, onMounted, watch, watchEffect } from 'vue';
import { useRoute, useRouter, RouteLocationRaw } from 'vue-router';
import { provideProjectTaskPolling } from '@/composables/project-task-polling';
import { useQuery } from '@vue/apollo-composable';
import { formatDate, deepParseDates } from '@/utils/date-utils';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { ProjectDocument, ProjectType, UserType } from '@/gql/graphql';
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BCollapse,
  BSpinner,
} from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';
import ProjectSampleAssociationCreate from '@/components/project/ProjectSampleAssociationCreate.vue';
import ProjectUserAssociationCreate from './project/ProjectUserAssociationCreate.vue';
import ProjectUserAssociationDelete from './project/ProjectUserAssociationDelete.vue';
import ProjectUpdate from '@/components/project/ProjectUpdate.vue';
import SampleList, {
  SampleTypeExtended,
} from '@/components/sample/SampleList.vue';
import { DefaultCode } from '@/components/CodeValidation.vue';
import {
  COMPUTE_TASK_TYPES,
  PAGING_ITEMS_PER_PAGE,
  SHARING_USER_ROLES,
  USER_ROLES,
  TRUNCATE_TEXT_LENGTH,
} from '@/constants';
import SampleReconstructionNextFlow from '@/components/compute/SampleReconstructionNextFlow.vue';
import NextflowTasks from '@/components/compute/NextflowTasks.vue';
import ComputeTasksNested from '@/components/compute/ComputeTasksNested.vue';
import ProjectDelete from '@/components/project/ProjectDelete.vue';
import { truncateText } from '@/utils/string-utils';
import { openRouterLink } from '@/utils/router-utils';

// --- Component References ---
const NextflowTasksRef = ref<InstanceType<typeof NextflowTasks>>();
const ComputeTasksNestedRef = ref<InstanceType<typeof ComputeTasksNested>>();
const hasLegacyReconstructions = computed(
  () =>
    !!ComputeTasksNestedRef.value &&
    !ComputeTasksNestedRef.value.tasksQueryInProgress &&
    ComputeTasksNestedRef.value.pagingTotalRows > 0
);

// --- Component properties ---
const props = defineProps({
  id: { type: Number, required: true },
});

// Compute the numeric projectId by converting the route param to a number
const projectId = computed(() => {
  const projectId = Number(props.id);
  if (isNaN(projectId)) {
    throw new Error('Invalid project ID');
  }
  return projectId;
});

const projectTaskPolling = provideProjectTaskPolling(projectId, () =>
  requestRefetchProjectDetails()
);

// --- Go Back Link ---
const backLinkTarget = computed<RouteLocationRaw>(() => {
  const q = route.query;
  if (q.returnTo) {
    const path = decodeURIComponent(String(q.returnTo));
    return { path }; // robust even if it has its own query/hash
  }
  // sensible fallback if user loaded the page directly
  return { name: 'Projects' };
});

const backLinkLabel = computed(() => {
  const q = route.query;
  if (q.returnLabel) {
    return `Back to ${String(q.returnLabel)}`;
  }
  // sensible fallback if user loaded the page directly
  return 'Go Back';
});

// --- Component state ---
const userStore = useUserStore();
const showCompleteDescription = ref(false);
const showFullSamples = ref(false);
const showAdvancedFeatures = ref(false);
const showProjectDeleteModal = ref(false);
const showProjectUpdateModal = ref(false);
const showProjectSampleAssociationCreateModal = ref(false);
const showProjectUserAssociationCreateModal = ref(false);
const showProjectUserAssociationDeleteModal = ref(false);
const showNewSampleReconstructionNextFlowModal = ref(false);
const selectedUser = ref<UserType | null>(null);

// Get the current route and router.
const route = useRoute();
const router = useRouter();

const activeBucketId = ref<number | null>(null);
const activeBucketPathPrefix = ref('');

// Watch the compute tasks (show samples if no compute tasks available)
const computeTasksCount = computed(() => {
  if (NextflowTasksRef.value && ComputeTasksNestedRef.value) {
    return (
      NextflowTasksRef.value.pagingTotalRows +
      ComputeTasksNestedRef.value.pagingTotalRows
    );
  }
  return null;
});

watchEffect(() => {
  const newCount = computeTasksCount.value;
  // collapse sample list if compute tasks not yet fetched, or fetched and not empty
  if (
    NextflowTasksRef.value?.tasksQueryInProgress ||
    ComputeTasksNestedRef.value?.tasksQueryInProgress ||
    newCount === null ||
    newCount > 0
  ) {
    showFullSamples.value = false;
    return;
  }
  // otherwise
  showFullSamples.value = true;
});

// When the currentPathPrefix changes, update the URL's 'path' parameter.
watch(activeBucketPathPrefix, (newPath) => {
  // Update only the 'path' parameter, preserving the rest.
  router.replace({
    query: { ...route.query, path: newPath },
  });

  // Scroll to expanded accordion item
  scrollToBucketTitle(activeBucketId.value);
});

// When the activeAccordion changes (i.e. user selects a different bucket),
// update the URL's 'bucket' parameter and reset the currentPathPrefix.
watch(activeBucketId, (newBucketId, oldBucketId) => {
  // Build new query parameters, always updating the bucket param.
  const newQuery = {
    ...route.query,
    path: activeBucketPathPrefix.value,
    bucket: newBucketId ? newBucketId.toString() : undefined,
  };

  // Only clear the 'path' if there was a previous bucket and it changes.
  if (oldBucketId !== null && newBucketId !== oldBucketId) {
    newQuery.path = '';
    activeBucketPathPrefix.value = '';
  }
  router.replace({ query: newQuery });

  // Scroll to expanded accordion item
  scrollToBucketTitle(newBucketId);
});

// Scroll to expanded accordion item, delay slightly to allow the accordion item to expand
function scrollToBucketTitle(bucketId: number | null) {
  if (bucketId !== null) {
    setTimeout(() => {
      const el = document.getElementById(`bucket-title-${bucketId}`);
      if (el) {
        //el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const elementY = window.scrollY + el.getBoundingClientRect().top;
        const y = elementY - 18;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 500);
  }
}

// When the component mounts, initialize these values from the URL query parameters.
onMounted(() => {
  // If the query parameter 'bucket' is present, use it as the active bucket id.
  if (route.query.bucket) {
    activeBucketId.value = Number(route.query.bucket) || null;
  }
  // If the query parameter 'path' is present, set it as the current folder path.
  if (route.query.path) {
    activeBucketPathPrefix.value = String(route.query.path);
  }
});

// --- User Projects ---
const project = computed<ProjectType | null>(() => {
  const projRes = projectQueryResult.value?.project;
  if (!projRes) {
    return null;
  }
  return deepParseDates(projRes) as ProjectType;
});

// --- Filtered Samples ---
const samples = computed(() => {
  const sampleList: SampleTypeExtended[] = [];
  project.value?.samplesAssociated?.forEach((assocSample) => {
    if (assocSample?.sample) {
      // check if user can edit sample
      const canEditSample = assocSample.sample.bucket?.usersAssociated?.some(
        (assocUser) =>
          assocUser !== null &&
          assocUser.userId === userStore.id &&
          (assocUser.userRole === SHARING_USER_ROLES.OWNER ||
            assocUser.userRole === SHARING_USER_ROLES.EDITOR)
      );

      // push sample to collection
      sampleList.push({
        ...assocSample.sample,
        canEdit: canEditSample || false,
      });
    }
  });
  return sampleList;
});

// --- Default Codes ---
const defaultCodes = computed(() => {
  let codeList: DefaultCode[] = [];
  project.value?.samplesAssociated?.forEach((assocSample) => {
    if (assocSample?.sample?.kit?.code?.code) {
      // insert new code into list, or increment units required
      const codeString = assocSample?.sample?.kit?.code?.code;
      const codeListIndex = codeList.findIndex(
        (code) => code.codeString === codeString
      );
      if (codeListIndex !== -1) {
        codeList[codeListIndex].unitsRequired++;
      } else {
        codeList.push({
          codeString: codeString,
          unitsRequired: 1,
        });
      }
    }
  });
  return codeList;
});

// --- User-Project Role ---
const userRole = computed(() => {
  const userAssoc = project.value?.usersAssociated?.find(
    (assoc) => assoc?.user?.id === userStore.id
  );
  if (userAssoc) {
    return userAssoc.userRole;
  }
  if (userStore.admin) {
    return USER_ROLES.ADMIN;
  }
  return null;
});

const userProjectAssociationError = computed(() => {
  return userRole.value === null && !projectQueryInProgress
    ? 'Access denied, user not associated with the project'
    : null;
});

const canEdit = computed(() => {
  return userRole.value === 'owner' || userRole.value === 'editor';
});
const isDeleted = computed(() => {
  return (
    project.value && project.value.deletedAt && project.value.deletedAt !== null
  );
});

/* GRAPHQL API DETAILS ***********************************************/
const {
  result: projectQueryResult,
  loading: projectQueryInProgress,
  error: projectQueryError,
  refetch: refetchProjectDetails,
} = useQuery(
  ProjectDocument,
  () => ({
    id: projectId.value,
  }),
  {
    enabled: computed(() => Boolean(projectId.value)),
  }
);

/* GRAPHQL API - RE-FETCH DATA WHEN VIEW ACTIVATED ***************************/
function requestRefetchProjectDetails() {
  refetchProjectDetails()
    ?.catch((err) => {
      projectQueryError.value = err;
      console.error('Refetch project details failed:', err);
    })
    ?.then(() => {
      projectQueryInProgress.value = false;
      projectQueryError.value = null;
      console.debug('Refetched project details');
    });
}

// --- Event Handlers ---
function newSampleAssocCreated() {
  showProjectSampleAssociationCreateModal.value = false;
  requestRefetchProjectDetails();
}

function newComputeTaskCreated() {
  void projectTaskPolling.refresh(true);
  showNewSampleReconstructionNextFlowModal.value = false;
  requestRefetchProjectDetails();
}

// --- Advanced Project Settings ---
function closeProjectUserAssociationCreateModal() {
  showProjectUserAssociationCreateModal.value = false;
}

function projectUserAssociationDeleteRequested(user: UserType) {
  selectedUser.value = user;
  showProjectUserAssociationDeleteModal.value = true;
}

function closeProjectUserAssociationDeleteModal() {
  showProjectUserAssociationDeleteModal.value = false;
}

function onProjectDeletedSuccess() {
  console.log('Project deleted successfully, redirecting to projects list');
  showProjectDeleteModal.value = false;
  router.push({ name: 'Projects' });
}

function handleBucketRequest(bucketId: number) {
  activeBucketId.value = bucketId;
}
</script>

<template>
  <div>
    <!-- Go Back Link -->
    <b-button
      @click="openRouterLink(backLinkTarget, $router, $event)"
      variant="outline-dark"
      class="mb-3"
      size="sm"
    >
      <font-awesome-icon icon="angle-left" class="me-2" />
      {{ backLinkLabel }}
    </b-button>

    <!-- Project name -->
    <template v-if="project?.name">
      <h1>
        <font-awesome-icon
          :icon="['fas', 'magnifying-glass-chart']"
          class="me-2"
        />
        <span class="me-2"> {{ project.name }}</span>
        <b-button-group class="p-0 link-dark align-baseline btn-xs">
          <b-button
            v-if="canEdit"
            @click="showProjectUpdateModal = true"
            :id="`btn-edit-project-${project.id}`"
            v-b-tooltip.hover
            title="Edit Project Details"
            variant="dark"
          >
            <font-awesome-icon :icon="['fas', 'pencil']" />
          </b-button>
          <!-- User Role Badge -->
          <b-button
            v-if="userRole === SHARING_USER_ROLES.OWNER"
            variant="warning"
          >
            <i class="fas fa-crown"></i>
            {{ userRole }}
          </b-button>
          <b-button
            v-else-if="userRole === SHARING_USER_ROLES.EDITOR"
            variant="danger"
          >
            <i class="fas fa-pencil"></i>
            {{ userRole }}
          </b-button>
          <b-button v-else variant="success">
            <i class="fas fa-eye"></i>
            viewer
          </b-button>
          <b-button variant="secondary">
            <i class="fas fa-calendar"></i>
            {{ formatDate(project?.createdAt) }}
          </b-button>
        </b-button-group>
      </h1>
      <hr />
    </template>

    <!-- Check if project was deleted, navigate user back to project list of to contact support -->
    <template v-if="isDeleted">
      <b-alert show v-model="isDeleted" variant="warning" class="mt-3">
        <font-awesome-icon icon="exclamation-triangle" class="me-2" />
        <strong>Project has been deleted</strong>
        <br />
        Nagivate back to the
        <router-link :to="{ name: 'Projects' }"
          >list of your active projects</router-link
        >
        or contact support for assistance.
      </b-alert>
    </template>
    <!-- PROJECT LOADING & DETAILS-->
    <div v-else class="my-3">
      <!-- Project Query Error Message -->
      <b-alert
        v-if="projectQueryError"
        v-model="projectQueryError"
        variant="danger"
        class="mt-3"
      >
        {{ projectQueryError.message }}
        <b-button
          @click="requestRefetchProjectDetails"
          variant="outline-dark"
          class="btn-sm"
        >
          <font-awesome-icon :icon="['fas', 'sync']" />
          Retry</b-button
        >
      </b-alert>

      <!-- User-Project Association Error (Access Denied) -->
      <b-alert
        v-model="userProjectAssociationError"
        variant="danger"
        class="mt-3"
      >
        {{ userProjectAssociationError }}
        <b-button
          @click="requestRefetchProjectDetails"
          variant="outline-dark"
          class="btn-sm"
        >
          <font-awesome-icon :icon="['fas', 'sync']" />
          Retry</b-button
        >
      </b-alert>

      <!-- Busy Loading -->
      <div v-if="projectQueryInProgress && !project">
        <b-spinner>Loading project details...</b-spinner>
      </div>

      <!-- PROJECT DETAILS -->
      <div v-if="project">
        <!-- Project Details -->
        <p v-if="project?.description">
          {{
            showCompleteDescription
              ? project.description
              : truncateText(project.description, TRUNCATE_TEXT_LENGTH)
          }}
          <BButton
            v-if="
              canEdit &&
              (showCompleteDescription ||
                project?.description.length <= TRUNCATE_TEXT_LENGTH)
            "
            @click="showProjectUpdateModal = true"
            variant="link"
            class="p-0 ms-2 link-dark align-baseline"
            size="sm"
          >
            <font-awesome-icon :icon="['fas', 'pencil']" />
          </BButton>
          <template v-if="project.description.length > TRUNCATE_TEXT_LENGTH">
            <span v-if="!showCompleteDescription">&nbsp;</span>
            <br v-else />
            <b-button
              variant="link"
              class="p-0 align-baseline link-secondary text-decoration-underline"
              :class="{ 'mt-2': showCompleteDescription }"
              @click="showCompleteDescription = !showCompleteDescription"
              :aria-expanded="showCompleteDescription"
            >
              {{ showCompleteDescription ? 'Show Less' : 'Show More' }}
            </b-button>
          </template>
        </p>

        <!-- Edit Project Details -->
        <ProjectUpdate
          v-if="showProjectUpdateModal && canEdit"
          :show="showProjectUpdateModal"
          :projectId="props.id"
          :projectName="project.name || ''"
          :projectDescription="project.description || ''"
          @close="showProjectUpdateModal = false"
          @closeAndRefresh="showProjectUpdateModal = false"
        />

        <!-- SAMPLES -->
        <!-- List samples -->
        <div v-if="samples">
          <h5
            @click="showFullSamples = !showFullSamples"
            class="mt-4"
            style="cursor: pointer"
            v-b-tooltip.hover
            :id="`samples-header-${project.id}`"
            :title="
              showFullSamples
                ? 'Click to collapse'
                : 'Click to show sample details'
            "
          >
            <template v-if="showFullSamples">
              <font-awesome-icon :icon="['fas', 'flask']" class="me-2" />
              Samples
            </template>
            <template v-else>
              <template v-if="samples.length === 0">
                <font-awesome-icon :icon="['fas', 'flask']" class="me-2" />
                No samples associated with this project
              </template>
              <template v-else>
                <b-button-group size="sm">
                  <b-button variant="dark" disabled>
                    <font-awesome-icon :icon="['fas', 'flask']" />
                  </b-button>
                  <b-button variant="dark">
                    <strong>
                      {{ samples.length }}
                      sample{{ samples.length === 1 ? '' : 's' }}
                    </strong>
                  </b-button>
                </b-button-group>
                associated with this project
              </template>
            </template>
            <font-awesome-icon
              :icon="['fas', showFullSamples ? 'chevron-up' : 'chevron-down']"
              class="ms-1"
            />
          </h5>
          <b-collapse v-model="showFullSamples">
            <SampleList
              :samples="samples"
              :loading="false"
              :canEdit="canEdit"
              :showBucket="true"
              :showKit="true"
              :showAssociatedProjects="false"
              :assocProjectId="props.id"
              :assocProjectName="project.name || 'undefined'"
              :canEditSampleAssoc="canEdit"
              @refresh="requestRefetchProjectDetails"
            />
            <BButton
              v-if="canEdit"
              @click="showProjectSampleAssociationCreateModal = true"
              variant="outline-dark"
              size="sm"
              class="me-2"
            >
              <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
              Add Sample
            </BButton>

            <div v-if="showProjectSampleAssociationCreateModal">
              <ProjectSampleAssociationCreate
                :show="showProjectSampleAssociationCreateModal"
                :projectId="props.id"
                :projectName="project.name || 'undefined'"
                @close="showProjectSampleAssociationCreateModal = false"
                @closeAndRefresh="newSampleAssocCreated"
              />
            </div>
          </b-collapse>
        </div>

        <!-- RECONSTRUCTION COMPUTE TASKS -->
        <h5 class="mt-4 mb-2">
          <font-awesome-icon
            :icon="['fas', 'wand-magic-sparkles']"
            class="me-2"
          />
          Sample Reconstruction
        </h5>
        <!-- NEW RECONSTRUCTION BUTTONS -->
        <BButton
          v-if="canEdit"
          @click="showNewSampleReconstructionNextFlowModal = true"
          variant="outline-dark"
          size="sm"
          class="my-3"
        >
          <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
          Sample Reconstruction
          <BBadge variant="warning">
            <i class="fab fa-neos"></i>
            Nextflow
          </BBadge>
        </BButton>
        <!-- NEW RECONSTRUCTION NEXT-FLOW -->
        <SampleReconstructionNextFlow
          v-if="showNewSampleReconstructionNextFlowModal"
          :show="showNewSampleReconstructionNextFlowModal"
          :projectId="projectId"
          :projectName="project?.name || 'undefined'"
          :unitsRequired="project?.samplesAssociated?.length || 0"
          :defaultCodes="defaultCodes"
          :defaultName="`Sample Reconstruction ${(computeTasksCount || 0) + 1}`"
          :samples="samples"
          @close="showNewSampleReconstructionNextFlowModal = false"
          @closeAndRefresh="newComputeTaskCreated"
        />

        <!-- LIST RECONSTRUCTION COMPUTE TASKS -->
        <h5 class="mt-4 mb-3">Nextflow reconstructions</h5>
        <NextflowTasks
          id="compute-tasks-nextflow"
          ref="NextflowTasksRef"
          :project-id="projectId"
          :paging-items-per-page="PAGING_ITEMS_PER_PAGE"
          :prevent-redirect-to-bucket="true"
          message-no-items-found="No Nextflow reconstruction tasks have been executed for this project."
          task-name-column-title="Name"
          @bucket-request="handleBucketRequest"
        />

        <!-- Keep mounted so task totals still drive the sample list. -->
        <div v-show="hasLegacyReconstructions">
          <h5 class="mt-4 mb-3">Legacy reconstructions</h5>
          <ComputeTasksNested
            id="compute-tasks-nested"
            ref="ComputeTasksNestedRef"
            :task-types="[
              COMPUTE_TASK_TYPES.COMPUTE_PIPELINE,
              COMPUTE_TASK_TYPES.DATA_PIPELINE,
            ]"
            :exclude-subtask-types="[
              COMPUTE_TASK_TYPES.NEXTFLOW_PIPELINE,
              COMPUTE_TASK_TYPES.FTP_TRANSFER,
              COMPUTE_TASK_TYPES.BASESPACE_TRANSFER,
              COMPUTE_TASK_TYPES.WASABI_TRANSFER,
              COMPUTE_TASK_TYPES.BCL_TO_FASTQ,
            ]"
            :project-id="projectId"
            :paging-items-per-page="PAGING_ITEMS_PER_PAGE"
            :prevent-redirect-to-bucket="true"
            message-no-items-found="No legacy reconstruction tasks have been executed for this project."
            task-name-column-title="Name"
            @bucket-request="handleBucketRequest"
          />
        </div>

        <!-- ADVANCED FEATURES - USERS and DELETE (show only to owners & editors) -->
        <template v-if="canEdit">
          <BButtonGroup class="mt-4" size="sm">
            <BButton
              @click="showAdvancedFeatures = !showAdvancedFeatures"
              variant="outline-dark"
            >
              <font-awesome-icon icon="cogs" class="me-1" />
              Advanced
              <font-awesome-icon
                :icon="showAdvancedFeatures ? 'angle-down' : 'angle-right'"
              />
            </BButton>
          </BButtonGroup>

          <BCollapse v-model="showAdvancedFeatures">
            <!-- USERS -->
            <h5 class="mt-5">
              <font-awesome-icon icon="user-shield" class="me-1" />
              Users & Sharing
            </h5>
            <p v-if="project?.usersAssociated?.length === 0">
              No users associated with this project.
            </p>
            <template
              v-for="(assoc, idx) in project?.usersAssociated"
              :key="idx"
            >
              <p v-if="assoc && assoc.user">
                <strong>Role:</strong> {{ assoc.userRole }}<br />
                <strong>Name:</strong>
                {{ assoc.user?.givenName }}
                {{ assoc.user?.familyName }}
                <br />
                <strong>Email:</strong> {{ assoc.user?.email }}<br />
                <b-button
                  v-if="canEdit && assoc.userRole !== 'owner'"
                  @click="projectUserAssociationDeleteRequested(assoc.user)"
                  variant="outline-danger"
                  size="sm"
                >
                  <font-awesome-icon :icon="['fas', 'times']" class="me-2" />
                  Remove
                </b-button>
              </p>
            </template>
            <BButton
              v-if="canEdit"
              @click="showProjectUserAssociationCreateModal = true"
              variant="outline-dark"
              size="sm"
            >
              <font-awesome-icon icon="plus" class="me-1" />
              Share project
            </BButton>
            <ProjectUserAssociationCreate
              v-if="showProjectUserAssociationCreateModal"
              :show="showProjectUserAssociationCreateModal"
              :projectId="props.id"
              :projectName="project.name || ''"
              @close="closeProjectUserAssociationCreateModal"
              @closeAndRefresh="closeProjectUserAssociationCreateModal"
            />
            <ProjectUserAssociationDelete
              v-if="showProjectUserAssociationDeleteModal && selectedUser"
              :show="showProjectUserAssociationDeleteModal"
              :projectId="props.id"
              :user="selectedUser"
              :projectName="project.name || ''"
              @close="closeProjectUserAssociationDeleteModal"
              @close-and-refresh="closeProjectUserAssociationDeleteModal"
            />
            <!-- end of users -->

            <!-- DELETE PROJECT -->
            <h5 class="mt-5">
              <font-awesome-icon icon="trash" class="me-1" />
              Delete Project
            </h5>
            <p variant="warning" show>
              <strong>Warning:</strong> This action is irreversible. The samples
              and their input data will remain in the system, but all associated
              data produced in the project will be removed. Please make sure to
              download any data you wish to keep before proceeding.
            </p>
            <b-button
              @click="showProjectDeleteModal = true"
              variant="danger"
              size="sm"
            >
              <font-awesome-icon icon="trash" class="me-1" />
              Delete Project
            </b-button>
          </BCollapse>
        </template>
        <!-- end of advanced features -->
      </div>
      <!-- end of project <div> -->
    </div>
    <!-- end of project loading & details <div> -->
    <!-- Project Delete Modal - must be located outside of <div v-if="!isDeleted"> -->
    <ProjectDelete
      v-if="showProjectDeleteModal && project && project.id"
      :show="showProjectDeleteModal"
      :projectId="project.id"
      :projectName="project.name || ''"
      @close="showProjectDeleteModal = false"
      @close-and-refresh="onProjectDeletedSuccess"
    />
  </div>
</template>
